import { useEffect, useRef, useState } from 'react'
import { getLinkPopUrl, openLinkPopUrl } from '../model/linkPop'

interface ILinkPopSession {
  scope: string
  active: boolean
  opened: boolean
  opening: boolean
  closed: boolean
  acknowledgement: 'idle' | 'pending' | 'success'
}

export function useLinkPopAction(
  url: string | undefined,
  onAcknowledge: () => boolean | void | Promise<boolean | void>,
  onDismiss: () => void,
  popIdentity?: string
) {
  const [openedScope, setOpenedScope] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<'open' | 'ack' | null>(null)

  const sessionRef = useRef<ILinkPopSession | null>(null)

  const safeUrl = getLinkPopUrl(url)
  const scope = JSON.stringify([popIdentity, url])
  const canClose = !!safeUrl && openedScope === scope

  const acknowledge = async (session: ILinkPopSession) => {
    if (session.acknowledgement !== 'idle') return

    session.acknowledgement = 'pending'

    try {
      const acknowledged = await onAcknowledge()

      if (session.active && !session.closed) {
        session.acknowledgement = acknowledged === false ? 'idle' : 'success'
        if (acknowledged === false) setError('ack')
      }
    } catch {
      if (session.active && !session.closed) {
        session.acknowledgement = 'idle'
        setError('ack')
      }
    }
  }

  const handleOpen = async () => {
    const session = sessionRef.current

    if (!session || session.scope !== scope || session.closed || session.opening || !safeUrl) return

    session.opening = true
    setIsPending(true)
    setError(null)

    try {
      if (!openLinkPopUrl(safeUrl)) {
        setError('open')
        return
      }

      session.opened = true
      setOpenedScope(scope)
      // Start ACK in the same click, without delaying navigation or waiting to enable X.
      void acknowledge(session)
    } catch {
      setError('open')
    } finally {
      // Ignore duplicate browser activations in the same tick, independently of the ACK request.
      await Promise.resolve()

      if (session.active && !session.closed) {
        session.opening = false
        setIsPending(false)
      }
    }
  }

  const handleClose = () => {
    const session = sessionRef.current

    if (!session || session.scope !== scope || session.closed || !session.opened || !safeUrl) return

    session.closed = true
    setOpenedScope(null)
    setIsPending(false)
    setError(null)
    onDismiss()
  }

  useEffect(() => {
    const session: ILinkPopSession = {
      scope,
      active: true,
      opened: false,
      opening: false,
      closed: false,
      acknowledgement: 'idle',
    }
    sessionRef.current = session
    setOpenedScope(null)
    setIsPending(false)
    setError(null)

    return () => {
      session.active = false
    }
  }, [scope])

  return { safeUrl, canClose, isPending, error, handleOpen, handleClose }
}
